import React, { useContext, useState, useEffect } from 'react';
import { Timestamp } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import {
  Space,
  Avatar,
  Row,
  Col,
  Collapse,
  Button,
  List,
  Timeline,
  message,
  Tabs,
  Input,
  Card,
  Divider,
} from 'antd';
import { useIntl } from 'react-intl';
import {
  CloseOutlined,
  CheckOutlined,
  UserAddOutlined,
  RetweetOutlined,
  SendOutlined,
} from '@ant-design/icons';
import { StoreContext } from '../store/index';
import { updateShift, addFriend } from '../shifts/api';
import { getDateFromTimestamp } from '../utils';
import Player from './player';
import TshirtIcon from '../components/tshirtIcon';
import PlayersList from '../components/PlayersList';
import StatsTab from './statsTab';

const Players = ({
  players,
  shift,
  timer,
  onInvite,
  onStatChangeInFinishedGame,
}) => {
  const { formatMessage } = useIntl();
  const store = useContext(StoreContext);
  const {
    user: [user, setUser, setUpdateUser],
  } = store;
  const navigate = useNavigate();

  const { playRequests, logs } = shift;

  const [loading, setLoading] = useState(false);
  const [messageInput, setMessageInput] = useState('');
  const [localPlayRequests, setLocalPlayRequests] = useState([]);
  const [localPlayers, setLocalPlayers] = useState([]);
  const [visitPlayRequests, setVisitPlayRequests] = useState([]);
  const [visitPlayers, setVisitPlayers] = useState([]);

  useEffect(() => {
    setLocalPlayers(players.filter(({ isLocal }) => isLocal));
    setLocalPlayRequests(playRequests.filter(({ isLocal }) => isLocal));
    setVisitPlayers(players.filter(({ isLocal }) => !isLocal));
    setVisitPlayRequests(playRequests.filter(({ isLocal }) => !isLocal));
  }, [players, playRequests]);

  const handleRejectPlayRequest = playRequest => {
    updateShift(
      shift.id,
      {
        playRequests: shift.playRequests.filter(
          r => r.userId !== playRequest.userId,
        ),
      },
      setLoading,
    );
  };

  const handleConfirmPlayRequest = playRequest => {
    let freeSpaces = shift.totalPlayers / 2;
    shift.players.forEach(p => {
      if (playRequest.isLocal === p.isLocal) {
        freeSpaces--;
      }
    });
    if (freeSpaces > 0) {
      updateShift(
        shift.id,
        {
          playRequests: shift.playRequests.filter(
            r => r.userId !== playRequest.userId,
          ),
          players: [...shift.players, playRequest],
        },
        setLoading,
      );
    } else {
      message.error('El equipo esta completo');
    }
  };
  const handleAddFriend = playerMail => {
    addFriend(user.uid, user.friendsIds, playerMail, setLoading);
    setUpdateUser(true);
  };

  const handleRemovePlayer = player => {
    updateShift(
      shift.id,
      {
        players: shift.players.filter(p => p.userId !== player.userId),
        playRequests: [
          ...shift.playRequests,
          {
            userId: player.userId,
            name: player.name,
            photoUrl: player.photoUrl,
            email: player.email,
            age: player.age,
            level: player.level,
            position: player.position,
            isLocal: player.isLocal,
            goals: 0,
            absent: false,
            yellowCard: 0,
            redCard: false,
            invitedById: player.invitedById ? player.invitedById : 0,
            invitedByName: player.invitedByName ? player.invitedByName : '',
            invitedByPhotoUrl: player.invitedByPhotoUrl
              ? player.invitedByPhotoUrl
              : '',
          },
        ],
      },
      setLoading,
    );
  };
  const handleSwapTeam = player => {
    let freeSpaces = shift.totalPlayers / 2;
    shift.players.forEach(p => {
      if (player.isLocal !== p.isLocal) {
        freeSpaces--;
      }
    });
    if (freeSpaces > 0) {
      updateShift(
        shift.id,
        {
          players: shift.players.map(p => ({
            ...p,
            isLocal: p.userId === player.userId ? !p.isLocal : p.isLocal,
          })),
        },
        setLoading,
      );
    } else message.error('No hay lugar en el otro equipo');
  };
  const handleSendMessage = () => {
    if (messageInput !== '') {
      updateShift(
        shift.id,
        {
          logs: [
            ...shift.logs,
            {
              date: Timestamp.fromDate(new Date()),
              userId: user.uid,
              name: user.nickname,
              type: 'message',
              message: messageInput,
            },
          ],
        },
        setLoading,
      );
      setMessageInput('');
    }
  };
  const completePlayerList = () => {
    const filledList = [];
    for (let i = 0; i < shift.totalPlayers / 2; i++) {
      const p = {
        local: localPlayers[i],
        visit: visitPlayers[i],
      };
      filledList.push(p);
    }
    return filledList;
  };
  const completePlayerRequestList = () => {
    const filledList = [];
    for (
      let i = 0;
      i < localPlayRequests.length + visitPlayRequests.length;
      i++
    ) {
      const p = {
        local: localPlayRequests[i],
        visit: visitPlayRequests[i],
      };
      filledList.push(p);
    }
    return filledList.filter(p => p.local || p.visit);
  };

  const getPlayersOnFilledList = rows => {
    let count = 0;
    rows.forEach(r => {
      if (r.local) {
        count++;
      }
      if (r.visit) {
        count++;
      }
    });
    return count;
  };

  const renderMessage = (date, value, type, name) => (
    <Row
      justify="space-between"
      style={{ margin: '4px 0px', paddingRight: '16px' }}>
      <Col>
        {type !== 'event' && <div>{name}</div>}
        <Card className="message">{value}</Card>
      </Col>
      <Col>{getDateFromTimestamp(date)}</Col>
    </Row>
  );

  const getTimelineLogs = () =>
    shift.logs.map(log => {
      if (log.type !== 'event') {
        return {
          children: log.message,
          label: log.name,
        };
      }
      return {
        children: log.message,
        label: getDateFromTimestamp(log.date),
        color: 'green',
      };
    });

  const renderPlayersList = (playerList, isRequest) => (
    <Collapse
      key={`${shift.id} ${isRequest}`}
      className="width100"
      style={{ marginTop: '2px' }}
      bordered={false}
      defaultActiveKey={isRequest ? null : [shift.id]}
      items={[
        {
          key: shift.id,
          label: `${
            isRequest
              ? formatMessage({ id: 'shifts.requests' })
              : `${formatMessage({
                  id: 'shifts.confirmed',
                })} ${getPlayersOnFilledList(playerList)}/${shift.totalPlayers}`
          } `,
          children: (
            <List
              size="small"
              itemLayout="vertical"
              loading={loading}
              defaultactivekey={['playersPanel']}
              dataSource={[...playerList]}
              header={
                <Row justify="space-between">
                  <Col xs={12}>
                    <Col xs={24}>
                      <Row justify="center">
                        <TshirtIcon size="70px" color={shift.localTeam.color} />
                      </Row>
                    </Col>
                    <Col xs={24}>
                      <Row justify="center">
                        <span>{`${shift.localTeam.name}`}</span>
                      </Row>
                    </Col>
                  </Col>
                  <Col xs={12}>
                    <Col xs={24}>
                      <Row justify="center">
                        <TshirtIcon size="70px" color={shift.visitTeam.color} />
                      </Row>
                    </Col>
                    <Col xs={24}>
                      <Row justify="center">
                        <span>{`${shift.visitTeam.name}`}</span>
                      </Row>
                    </Col>
                  </Col>
                </Row>
              }
              renderItem={player => (
                <List.Item
                  style={{ padding: '0px', marginTop: '8px', border: 'none' }}
                  key={player.id}>
                  <Row
                    justify="center"
                    style={{
                      marginBottom: '8px',
                    }}>
                    <Player
                      player={player.local}
                      shift={shift}
                      isRequest={isRequest}
                      handleAddFriend={handleAddFriend}
                      handleRejectPlayRequest={handleRejectPlayRequest}
                      handleConfirmPlayRequest={handleConfirmPlayRequest}
                      handleSwapTeam={handleSwapTeam}
                      handleRemovePlayer={handleRemovePlayer}
                      isLocal
                      onInvite={onInvite}
                    />
                    <Divider type="vertical" style={{ heigth: 'auto' }} />
                    <Player
                      player={player.visit}
                      shift={shift}
                      isRequest={isRequest}
                      handleAddFriend={handleAddFriend}
                      handleRejectPlayRequest={handleRejectPlayRequest}
                      handleConfirmPlayRequest={handleConfirmPlayRequest}
                      handleSwapTeam={handleSwapTeam}
                      handleRemovePlayer={handleRemovePlayer}
                      isLocal={false}
                      onInvite={onInvite}
                    />
                  </Row>
                </List.Item>
              )}
            />
          ),
        },
      ]}
    />
  );

  return (
    <Row justify="space-between" className="width100">
      <Tabs
        className="width100"
        items={[
          {
            key: 1,
            label: formatMessage({ id: 'shifts.teams' }),
            children: (
              <>
                <Row>{renderPlayersList(completePlayerList(), false)}</Row>
                {!shift.isFinished && (
                  <Row>
                    {renderPlayersList(completePlayerRequestList(), true)}
                  </Row>
                )}
              </>
            ),
          },
          {
            key: 2,
            label: formatMessage({ id: 'shifts.chat' }),
            children: (
              <div>
                <Timeline
                  style={{
                    paddingTop: '2px',
                    overflow: 'auto',
                    maxHeight: '300px',
                  }}
                  mode="right"
                  items={getTimelineLogs()}
                />
                <Space.Compact
                  style={{
                    paddingRight: '16px',
                    margin: '8px 0px',
                    width: '100%',
                  }}>
                  <Input
                    value={messageInput}
                    placeholder={formatMessage({
                      id: 'shifts.actions.send.message',
                    })}
                    onChange={e => setMessageInput(e.target.value)}
                  />
                  <Button
                    type="primary"
                    loading={loading}
                    disabled={
                      !user.isAdmin || !players.find(p => p.userId === user.uid)
                    }
                    icon={<SendOutlined />}
                    onClick={handleSendMessage}
                  />
                </Space.Compact>
              </div>
            ),
          },
          {
            key: 3,
            label: formatMessage({ id: 'shifts.stats' }),
            children: (
              <StatsTab
                shift={shift}
                timer={timer}
                onStatChangeInFinishedGame={onStatChangeInFinishedGame}
              />
            ),
          },
        ]}
      />
    </Row>
  );
};
export default Players;
